import { LoadingOutlined } from '@ant-design/icons';
import {
  BarChartIcon,
  CalendarDateRangeIcon,
  ContrastIcon,
  DownloadIconAlt,
  RetryIcon,
  SatelliteIcon,
  TimeSeriesIcon,
} from '@assets/icons/minor';
import { AnalyticsAPI } from '@core/api';
import { Reports } from '@core/models/reports/reports.models';
import dayjs from '@utils/dateUtils';
import { Spin } from 'antd';
import type { ComponentType, SVGProps } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ReportListItemProps {
  report: Reports.ReportQueryData;
  onRetry?: (report: Reports.ReportQueryData) => void;
  onDownload?: (fileUrl: string, report: Reports.ReportQueryData) => void;
}

const BULK_XLSX_TYPES = new Set<Reports.ReportTypes>([
  Reports.ReportTypes.TIME_SERIES_XLSX,
  Reports.ReportTypes.HISTOGRAM_XLSX,
]);

const REPORT_CONFIG: Record<
  Reports.ReportTypes,
  {
    icon: ComponentType<SVGProps<SVGSVGElement>>;
    iconBgColor: string;
    iconColor: string;
    label: string;
  }
> = {
  [Reports.ReportTypes.NDVI_CHANGE]: {
    icon: SatelliteIcon,
    iconBgColor: 'bg-sy-neutral-10',
    iconColor: '#14803C',
    label: 'header.reports.drawer.ndvi_change_report',
  },
  [Reports.ReportTypes.VARIABILITY_CHANGE]: {
    icon: ContrastIcon,
    iconBgColor: 'bg-sy-neutral-10',
    iconColor: '#744A0B',
    label: 'header.reports.drawer.variability_change_report',
  },
  [Reports.ReportTypes.FIELD_INSIGHTS]: {
    icon: CalendarDateRangeIcon,
    iconBgColor: 'bg-sy-neutral-10',
    iconColor: '#217C74',
    label: 'header.reports.drawer.weekly_field_insights_report',
  },
  [Reports.ReportTypes.TIME_SERIES_XLSX]: {
    icon: TimeSeriesIcon,
    iconBgColor: 'bg-sy-neutral-10',
    iconColor: '#0071CD',
    label: 'header.reports.drawer.time_series_xlsx_report',
  },
  [Reports.ReportTypes.HISTOGRAM_XLSX]: {
    icon: BarChartIcon,
    iconBgColor: 'bg-sy-neutral-10',
    iconColor: '#BB485D',
    label: 'header.reports.drawer.histogram_xlsx_report',
  },
};

const ReportListItem: React.FC<ReportListItemProps> = ({ report, onRetry, onDownload }) => {
  const { t } = useTranslation();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const config = REPORT_CONFIG[report.type];

  if (!config) return null;

  const IconComponent = config.icon;

  const isGenerating = report.status === Reports.ReportStatus.GENERATING;
  const isError = report.status === Reports.ReportStatus.ERROR;
  const isAvailable = report.status === Reports.ReportStatus.AVAILABLE;

  const handleDownload = async () => {
    if (!report.file_url || !onDownload) return;

    let fileUrl = report.file_url;

    if (BULK_XLSX_TYPES.has(report.type) && report.report_id) {
      setIsRefreshing(true);
      try {
        const { data } = await AnalyticsAPI.get<Reports.ReportDTO>(`/reports/${report.report_id}`);
        fileUrl = data.file_url ?? fileUrl;
      } catch (err) {
        console.warn('Failed to refresh bulk report URL, falling back to stored URL:', err);
      } finally {
        setIsRefreshing(false);
      }
    }

    onDownload(fileUrl, report);
  };

  const handleRetry = () => {
    onRetry?.(report);
  };

  const showNewTag = report.is_new && isAvailable;

  const getStatusText = () => {
    if (isGenerating) return t('header.reports.drawer.status_generating');
    if (isError) return t('header.reports.drawer.status_error');
    if (isAvailable && report.generated_at) {
      const generatedDate = dayjs(report.generated_at);
      return generatedDate.isSame(dayjs(), 'day')
        ? `${t('common.today')} • ${generatedDate.format('LT')}`
        : generatedDate.format('L • LT');
    }
    return '';
  };

  const statusTextColor = isError ? 'text-sy-red-60' : 'text-sy-neutral-60';

  return (
    <div className="relative w-full mt-1 h-12 border-b border-sy-neutral-20 flex items-center pr-2">
      <div className="flex-shrink-0 mr-3">
        <div
          className={`${config.iconBgColor} rounded size-6 p-0.5 flex items-center justify-center`}
        >
          <IconComponent width={18} height={18} fill={config.iconColor} />
        </div>
      </div>
      <div className="flex-1 flex flex-col justify-center min-w-0">
        <div className="flex items-center gap-1 mb-0.5">
          <p className="m-0 text-sm text-sy-neutral-100 font-normal leading-5 truncate">
            {t(config.label)}
          </p>
          {showNewTag && (
            <div className="bg-sy-blue-20 border border-sy-blue-20 rounded px-1 flex items-center flex-shrink-0">
              <span className="text-xs text-sy-blue-70 font-semibold leading-4 uppercase whitespace-nowrap">
                {t('common.new')}
              </span>
            </div>
          )}
        </div>

        {(!isAvailable || report.generated_at) && (
          <p className={`m-0 text-xs font-normal leading-5 ${statusTextColor}`}>
            {getStatusText()}
          </p>
        )}
      </div>
      <div className="flex-shrink-0 ml-2">
        {isGenerating && (
          <div className="size-[18px]">
            <Spin indicator={<LoadingOutlined spin />} />
          </div>
        )}

        {isError && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-sy-blue-60 font-normal leading-5">
              {t('header.reports.drawer.try_again')}
            </span>
            <button
              type="button"
              data-testid={`retry-report-${report.type}-button`}
              onClick={handleRetry}
              className="bg-sy-neutral-10 hover:bg-sy-neutral-20 rounded-full size-6 flex items-center justify-center border-0 cursor-pointer p-0 transition-colors"
            >
              <RetryIcon width={18} height={18} />
            </button>
          </div>
        )}

        {isAvailable &&
          (isRefreshing ? (
            <div className="size-[18px]">
              <Spin indicator={<LoadingOutlined spin />} />
            </div>
          ) : (
            <button
              type="button"
              data-testid={`download-report-${report.type}-button`}
              onClick={handleDownload}
              className="bg-sy-neutral-10 hover:bg-sy-neutral-20 rounded-full size-6 flex items-center justify-center border-0 cursor-pointer p-0 transition-colors"
            >
              <DownloadIconAlt width={18} height={18} />
            </button>
          ))}
      </div>
    </div>
  );
};

export default ReportListItem;
